import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useNotificationCount,
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useAcceptInvitation,
  useDeclineInvitation,
} from '@/hooks/useApi';
import toast from 'react-hot-toast';
import { BellIcon } from '@heroicons/react/24/outline';
import type { AppNotification } from '@/types';

function getNotificationLink(n: AppNotification): string | null {
  if (!n.data) return null;
  if (n.data.quoteId) return `/quotes/${n.data.quoteId}`;
  if (n.data.invoiceId) return `/invoices/${n.data.invoiceId}`;
  if (n.data.expenseId) return `/expenses/${n.data.expenseId}`;
  if (n.data.clientId) return `/clients/${n.data.clientId}`;
  return null;
}

const TYPE_ICONS: Record<string, string> = {
  quote_accepted: '✅',
  quote_rejected: '❌',
  quote_viewed: '👁️',
  invoice_paid: '💰',
  invoice_overdue: '⚠️',
  organization_invitation: '✉️',
  invitation_accepted: '🎉',
  invitation_declined: '🚫',
};

export default function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: count = 0 } = useNotificationCount();
  const { data: notifications = [] } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const acceptInvitation = useAcceptInvitation();
  const declineInvitation = useDeclineInvitation();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleAccept = async (token: string, orgName: string) => {
    try {
      await acceptInvitation.mutateAsync(token);
      toast.success(t('notifications.invitationAccepted', { org: orgName }));
      setOpen(false);
      navigate('/team');
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDecline = async (token: string) => {
    try {
      await declineInvitation.mutateAsync(token);
      toast.success(t('notifications.invitationDeclined'));
      setOpen(false);
      navigate('/team');
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label={t('notifications.title')}
      >
        <BellIcon className="w-6 h-6" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-[60] overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">{t('notifications.title')}</span>
            {count > 0 && (
              <button
                className="text-xs text-primary-600 hover:underline"
                onClick={() => markAllRead.mutate()}
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {notifications.length === 0 ? (
              <div className="text-center py-10 px-4">
                <BellIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">{t('notifications.empty')}</p>
              </div>
            ) : (
              notifications.map((n: AppNotification) => {
                const link = getNotificationLink(n);
                const icon = TYPE_ICONS[n.type] || '🔔';
                const isInvitation = n.type === 'organization_invitation' && n.data?.invitationToken;
                return (
                  <div
                    key={n.id}
                    className={`px-4 py-3 hover:bg-gray-50 ${isInvitation ? '' : 'cursor-pointer'} ${!n.readAt ? 'bg-blue-50/50' : ''}`}
                    onClick={() => {
                      if (isInvitation) return;
                      if (!n.readAt) markRead.mutate(n.id);
                      if (link) {
                        navigate(link);
                        setOpen(false);
                      }
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 text-base flex-shrink-0">{icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{n.title}</p>
                        {n.message && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>}
                        <p className="text-xs text-gray-400 mt-1">{new Date(n.createdAt).toLocaleString()}</p>
                        {isInvitation && (
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={acceptInvitation.isPending || declineInvitation.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAccept(
                                  n.data!.invitationToken,
                                  n.data?.organizationName || ''
                                );
                              }}
                            >
                              {t('notifications.accept')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              disabled={acceptInvitation.isPending || declineInvitation.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDecline(n.data!.invitationToken);
                              }}
                            >
                              {t('notifications.decline')}
                            </button>
                          </div>
                        )}
                      </div>
                      {!n.readAt && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary-500 flex-shrink-0" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
